import React from "react"
import verifiedIcon from "../../../assets/images/user/verified.png"
import VerifiedBadge from "../../VerifiedBadge"
import {
  getReviewCountsByRating,
  getReviewSources,
  getReviewCountsBySource,
  getReviewSource,
  getReviewRating,
  getReviewTimestamp,
  calculateAverageRating
} from "../utils/reviewUtils"

const ProfileHeader = ({ 
  lawyer, 
  reviews, 
  onBookAppointment, 
  onCallLawyer, 
  onSendEnquiry, 
  onLeaveReview,
  onVisitWebsite,
  onClaimListing,
  currentUser,
  canClaimListing
}) => {
  return (
    <div className="profile-header bg-gradient-to-r from-indigo-50 to-purple-50 p-8 relative rounded-3xl">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Left Column - Lawyer Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl md:text-4xl font-bold" style={{
              background: "linear-gradient(90deg, #D80364 31.41%, #F0654B 69.97%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}>
              {lawyer.name}
            </h1>
            {/* {lawyer.isVerified && (
              <VerifiedBadge />
            )} */}
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-6">
            {/* Rating Stars - only show if there are actual reviews with a rating */}
            {reviews && ((reviews.reviews && reviews.reviews.length > 0) || (reviews.stats && reviews.stats.total_reviews > 0)) && (
              <div className="flex items-center">
                {(() => {
                  const avgRating = calculateAverageRating(reviews);
                  const totalReviews = reviews.stats?.total_reviews || reviews.reviews?.length || 0;
                  
                  // Only show stars if we have a valid rating (not null)
                  if (avgRating !== null && avgRating > 0) {
                    return (
                      <>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-5 h-5 ${i < avgRating ? "text-yellow-400" : "text-gray-300"}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                            </svg>
                          ))}
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                          {avgRating} ({totalReviews} {totalReviews === 1 ? "review" : "reviews"})
                        </span>
                      </>
                    );
                  } else if (totalReviews > 0) {
                    // If we have reviews but no rating, just show the count
                    return (
                      <span className="text-sm text-gray-600">
                        ({totalReviews} {totalReviews === 1 ? "review" : "reviews"})
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Location */}
            {lawyer.location && (
              <div className="flex items-center text-gray-600">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  ></path>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
                <span className="text-sm">{lawyer.location}</span>
              </div>
            )}

            {/* Claim Status Indicator (if applicable) */}
            {lawyer.is_claimed && lawyer.claim_status === 'pending' && (
              <div className="flex items-center text-gray-600">
                <svg
                  className="w-4 h-4 mr-1 text-amber-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full">
                  Verification Pending
                </span>
              </div>
            )}

            {/* Verified Account */}
            {lawyer.isVerified && (
              <div className="flex items-center text-gray-600">
                <img src={verifiedIcon || "/placeholder.svg"} alt="Verified" className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Verified Account</span>
              </div>
            )}
          </div>

          {/* Primary Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={onBookAppointment}
              className="px-5 py-2.5 text-white font-medium rounded-full transition-colors shadow-sm flex items-center"
              style={{
                background: "linear-gradient(92.68deg, #D80364 0%, #F0654B 100%)"
              }}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"></path>
              </svg>
              Book an appointment
            </button>
            <button
              onClick={onCallLawyer}
              className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-full transition-colors shadow-sm flex items-center"
              disabled={
                !lawyer.contactDetails ||
                !lawyer.contactDetails.phone ||
                lawyer.contactDetails.phone === "Not Available"
              }
            >
              <svg
                className="w-4 h-4 mr-2 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                ></path>
              </svg>
              Call now
            </button>
            <button
              onClick={onSendEnquiry}
              className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-full transition-colors shadow-sm flex items-center"
            >
              <svg
                className="w-4 h-4 mr-2 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                ></path>
              </svg>
              Send enquiry
            </button>
          </div>

          {/* Secondary Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onLeaveReview}
              className="px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-600 text-sm font-medium rounded-full transition-colors flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                ></path>
              </svg>
              Leave a review
            </button>
            <button
              onClick={onVisitWebsite}
              className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm font-medium rounded-full transition-colors flex items-center"
              disabled={
                !lawyer.contactDetails ||
                !lawyer.contactDetails.website ||
                lawyer.contactDetails.website === "Not Available"
              }
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                ></path>
              </svg>
              Visit website
            </button>
            {/* Claim Listing Button for Legal Professionals */}
            {(currentUser?.role === 'legal_professional' || currentUser?.role_name === 'legal_professional') && 
             canClaimListing && (
              <button
                onClick={onClaimListing}
                className="px-4 py-2 bg-pink-50 hover:bg-pink-100 text-pink-600 text-sm font-medium rounded-full transition-colors flex items-center"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  ></path>
                </svg>
                Claim this Listing
              </button>
            )}
          </div>
        </div>

        {/* Right Column - Organisation Logo */}
        <div className="md:w-1/3 lg:w-2/5">
          <div className="relative h-64 rounded-2xl overflow-hidden group">
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-blue-600/10 to-indigo-600/10 animate-pulse"></div>
            
            {/* Glassmorphism container */}
            <div className="absolute inset-2 bg-white/80 backdrop-blur-xl rounded-xl border border-white/30 shadow-2xl transition-all duration-500 group-hover:shadow-purple-500/20 group-hover:border-purple-300/50">
              
              {/* Content */}
              <div className="h-full flex items-center justify-center p-6">
                {lawyer.logoUrl ? (
                  <div className="relative">
                    {/* Logo glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-400/20 blur-xl rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Main logo */}
                    <img
                      src={lawyer.logoUrl}
                      alt={`${lawyer.name} Logo`}
                      className="relative z-10 max-h-28 max-w-[180px] w-auto h-auto object-contain transition-all duration-500 group-hover:scale-110"
                      style={{ 
                        filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.15)) drop-shadow(0 4px 6px rgba(139,118,227,0.2))'
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center">
                    {/* Icon container with gradient */}
                    <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-100 via-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                      <div className="relative">
                        {/* Icon glow */}
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 blur-md opacity-30 rounded-lg"></div>
                        <svg
                          className="relative w-12 h-12 text-transparent bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text"
                          fill="none"
                          stroke="url(#gradient)"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#8B76E3" />
                              <stop offset="100%" stopColor="#3B82F6" />
                            </linearGradient>
                          </defs>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Text with gradient */}
                    <h3 className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent font-bold text-lg mb-1">
                      Legal Professional
                    </h3>
                    <p className="text-gray-500 text-sm font-medium">
                      Regulated Legal Services
                    </p>
                  </div>
                )}
              </div>
              
              {/* Floating particles */}
              <div className="absolute top-4 left-4 w-1 h-1 bg-purple-400/60 rounded-full animate-ping"></div>
              <div className="absolute top-8 right-6 w-1.5 h-1.5 bg-blue-400/50 rounded-full animate-pulse"></div>
              <div className="absolute bottom-6 left-6 w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full opacity-40 animate-bounce" style={{ animationDelay: '1s' }}></div>
              
              {/* Corner decorations */}
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-purple-200/30 to-transparent rounded-bl-2xl"></div>
              <div className="absolute bottom-0 left-0 w-12 h-12 bg-gradient-to-tr from-blue-200/30 to-transparent rounded-tr-2xl"></div>
            </div>
            
            {/* Outer glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/5 to-blue-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
          </div>

          {/* Accreditations */}
          {lawyer.accreditations && lawyer.accreditations.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Accredited by</h3>
              <div className="flex flex-wrap gap-4">
                {lawyer.accreditations.map((accreditation, index) => (
                  <div key={index} className="bg-white p-2 rounded-lg shadow-sm border border-gray-100">
                    <img
                      src={accreditation.logo_url || "/placeholder.svg"}
                      alt={typeof accreditation === 'object' && accreditation !== null ? 
                            (accreditation.name || 'Accreditation') : 
                            (accreditation || 'Accreditation')}
                      className="h-8 w-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfileHeader 